import { createContext, Dispatch, SetStateAction, useCallback, useContext, useMemo, useState } from 'react';
import { backendServices } from '../../../../services';
import { documentLayoutToFormState } from './documentLayoutToFormState';
import { FieldDictionaryViewModelWithMetadata, FormState } from './types';

type FieldDictionaryEditorContextType = {
    documentLayout: backendServices.ViewModels.DocumentLayoutViewModel;

    formState: FormState;

    setFormState: Dispatch<SetStateAction<FormState>>;
};

const FieldDictionaryEditorContext = createContext<FieldDictionaryEditorContextType | null>(null);

export function FieldDictionaryEditorProvider({
    children,
    documentLayout,
}: {
    children: React.ReactNode;
    documentLayout: backendServices.ViewModels.DocumentLayoutViewModel;
}) {
    const [formState, setFormState] = useState(() => documentLayoutToFormState(documentLayout));

    return (
        <FieldDictionaryEditorContext.Provider value={{ documentLayout, formState, setFormState }}>
            {children}
        </FieldDictionaryEditorContext.Provider>
    );
}

export function useFieldDictionaryEditorState() {
    const context = useContext(FieldDictionaryEditorContext);

    if (!context) {
        throw new Error('useFieldDictionaryEditorState must be used within a FieldDictionaryEditorProvider');
    }

    return context;
}

export function useSelectedFieldDictionaryId() {
    const { formState } = useFieldDictionaryEditorState();

    return formState.selectedFieldDictionaryId;
}

export function useFieldDictionaryWithMetadata(id: number) {
    const { formState } = useFieldDictionaryEditorState();

    const fieldDictionaryWithMetadata = formState.uniqueFieldDictionaries.get(id);

    if (!fieldDictionaryWithMetadata) {
        throw new Error(`Field dictionary with ID ${id} not found`);
    }

    return fieldDictionaryWithMetadata;
}

export function useOriginalFieldDictionaryWithMetadata(id: number) {
    const { formState } = useFieldDictionaryEditorState();

    const fieldDictionaryWithMetadata = formState.originalFieldDictionaries.get(id);

    if (!fieldDictionaryWithMetadata) {
        throw new Error(`Original field dictionary with ID ${id} not found`);
    }

    return fieldDictionaryWithMetadata;
}

export function useFieldDictionariesByIds(ids: number[]) {
    const { formState } = useFieldDictionaryEditorState();

    const fieldDictionaries = useMemo(() => {
        return ids
            .map((id) => formState.uniqueFieldDictionaries.get(id))
            .filter((fd): fd is NonNullable<typeof fd> => fd != null);
    }, [formState.uniqueFieldDictionaries, ids]);

    return fieldDictionaries;
}

export function useFieldDictionariesBySection() {
    const { formState } = useFieldDictionaryEditorState();

    return formState.fieldDictionariesBySection;
}

export function useSetSelectedFieldDictionaryId() {
    const { setFormState } = useFieldDictionaryEditorState();

    return useCallback(
        (id: number) => {
            setFormState((prev) => ({ ...prev, selectedFieldDictionaryId: id }));
        },
        [setFormState]
    );
}

export function useSetSearchQuery() {
    const { setFormState } = useFieldDictionaryEditorState();

    return useCallback(
        (query: string) => {
            setFormState((prev) => ({ ...prev, searchQuery: query }));
        },
        [setFormState]
    );
}

export function useSearchQuery() {
    const { formState } = useFieldDictionaryEditorState();

    return formState.searchQuery;
}

type FieldDictionaryUpdater = (prev: FieldDictionaryViewModelWithMetadata) => FieldDictionaryViewModelWithMetadata;

export function useSetFieldDictionary() {
    const { setFormState } = useFieldDictionaryEditorState();

    return useCallback(
        (id: number, updater: FieldDictionaryUpdater) => {
            setFormState((prev) => ({
                ...prev,
                uniqueFieldDictionaries: new Map(
                    prev.uniqueFieldDictionaries.set(id, updater(prev.uniqueFieldDictionaries.get(id)!))
                ),
            }));
        },
        [setFormState]
    );
}
